import React, { createContext, useReducer, ReactNode, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useFlagsmith } from 'flagsmith/react';
import { environment } from '../environment/environment';
import { localStorageService } from '../services';
import { userAuthApiService } from '../services/UserAuthApiService';
// import { SessionTimeoutManager } from '../services/SessionTimeoutManager';
// import { INACTIVITY_TIMEOUT_MS } from '../config/sessionTimeout';
import { User } from '../types/UserTypes';

interface AuthResult {
  success: boolean;
  user?: User;
  error?: any;
}

interface GlobalState {
  advances: number;
  registrations: number;
  user: User | null;
  risky_advances?: number;
  supportTickets?: number;
}

interface Action {
  type: 'SET_ADVANCES' | 'SET_RISKY_ADVANCES' | 'SET_REGISTRATIONS' | 'SET_SUPPORT_TICKETS' | 'SET_USER';
  payload: any;
}

interface GlobalStoreContextType {
  state: GlobalState;
  fetchStats: () => void;
  loginUser: () => Promise<AuthResult>;
  logOutUser: () => Promise<void>;
}

interface GlobalStoreProps {
  children: ReactNode;
}

const initialState: GlobalState = {
  advances: 0,
  registrations: 0,
  user: localStorageService.user,
};

const Reducer = (state: GlobalState, action: Action): GlobalState => {
  switch (action.type) {
    case 'SET_ADVANCES':
      return {
        ...state,
        advances: action.payload,
      };
    case 'SET_RISKY_ADVANCES':
      return {
        ...state,
        risky_advances: action.payload,
      };
    case 'SET_REGISTRATIONS':
      return {
        ...state,
        registrations: action.payload,
      };
    case 'SET_SUPPORT_TICKETS':
      return {
        ...state,
        supportTickets: action.payload,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

const GlobalStore = ({ children }: GlobalStoreProps) => {
  const [state, dispatch] = useReducer(Reducer, initialState);
  const flagsmith = useFlagsmith();
  const { getAccessTokenSilently, logout: auth0Logout } = useAuth0();
  // const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  // const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Basic state management functions
  const setUserInStore = useCallback((user: User | null): void => {
    if (user) {
      localStorageService.user = user;
      dispatch({ type: 'SET_USER', payload: user });
    } else {
      localStorageService.removeUser();
      dispatch({ type: 'SET_USER', payload: null });
    }
  }, []);

  const clearUserFromStore = useCallback((): void => {
    setUserInStore(null);
  }, [setUserInStore]);

  const loginUser = useCallback(async (): Promise<AuthResult> => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      const user = await userAuthApiService.login({ token });
      await flagsmith.identify(user.email);
      setUserInStore(user);

      return { success: true, user };
    } catch (error) {
      return { success: false, error };
    }
  }, [getAccessTokenSilently, flagsmith, setUserInStore]);

  // Clear inactivity timer
  // const clearInactivityTimer = useCallback((): void => {
  //   if (inactivityTimerRef.current) {
  //     clearTimeout(inactivityTimerRef.current);
  //     inactivityTimerRef.current = null;
  //   }
  // }, []);

  // Logout user
  const logOutUser = useCallback(async (): Promise<void> => {
    // clearInactivityTimer();
    // SessionTimeoutManager.clear();

    try {
      await userAuthApiService.logout();
    } catch (error) {
      // Continue with logout even if backend call fails
    }

    clearUserFromStore();

    auth0Logout({
      logoutParams: {
        returnTo: `${globalThis.location.origin}/auth/login`,
      },
    });
  }, [auth0Logout, clearUserFromStore]); // clearInactivityTimer

  // Start inactivity timer
  // const startInactivityTimer = useCallback((): void => {
  //   clearInactivityTimer();
  //   inactivityTimerRef.current = setTimeout(() => {
  //     logOutUser();
  //   }, INACTIVITY_TIMEOUT_MS);
  // }, [clearInactivityTimer, logOutUser]);

  // Reset inactivity timer on user activity
  // const resetInactivityTimer = useCallback((): void => {
  //   if (state.user) {
  //     startInactivityTimer();
  //   }
  // }, [state.user, startInactivityTimer]);

  const fetchStats = useCallback(async (): Promise<void> => {
    axios.get(`${environment.baseUrl}/stats/sidebar/`).then((res) => {
      dispatch({ type: 'SET_ADVANCES', payload: res.data.advances });
      dispatch({
        type: 'SET_RISKY_ADVANCES',
        payload: res.data.risky_advances,
      });
      dispatch({ type: 'SET_REGISTRATIONS', payload: res.data.registrations });
      dispatch({
        type: 'SET_SUPPORT_TICKETS',
        payload: res.data.support_tickets,
      });
    });
  }, []);

  // Check for absolute session timeout on mount and when user changes
  // useEffect(() => {
  //   if (state.user && SessionTimeoutManager.hasSessionExpired()) {
  //     logOutUser();
  //     return;
  //   }

  //   if (state.user) {
  //     startInactivityTimer();
  //   } else {
  //     clearInactivityTimer();
  //   }

  //   return () => clearInactivityTimer();
  // }, [state.user, logOutUser, startInactivityTimer, clearInactivityTimer]);

  // Set up activity listeners to reset inactivity timer
  // useEffect(() => {
  //   const activityEvents = ['mousedown', 'keydown', 'touchstart', 'focus'];

  //   const handleActivity = () => {
  //     resetInactivityTimer();
  //   };

  //   const handleScroll = () => {
  //     if (scrollTimeoutRef.current) {
  //       clearTimeout(scrollTimeoutRef.current);
  //     }
  //     scrollTimeoutRef.current = setTimeout(() => {
  //       resetInactivityTimer();
  //     }, 300);
  //   };

  //   if (state.user) {
  //     activityEvents.forEach(event => {
  //       document.addEventListener(event, handleActivity, true);
  //     });
  //     document.addEventListener('scroll', handleScroll, true);
  //   }

  //   return () => {
  //     activityEvents.forEach(event => {
  //       document.removeEventListener(event, handleActivity, true);
  //     });
  //     document.removeEventListener('scroll', handleScroll, true);
  //     if (scrollTimeoutRef.current) {
  //       clearTimeout(scrollTimeoutRef.current);
  //     }
  //   };
  // }, [state.user, resetInactivityTimer]);

  const contextValue = useMemo(
    () => ({
      state,
      fetchStats,
      loginUser,
      logOutUser,
    }),
    [state, fetchStats, loginUser, logOutUser]
  );

  return (
    <GlobalStoreContext.Provider value={contextValue}>
      {children}
    </GlobalStoreContext.Provider>
  );
};

export const GlobalStoreContext = createContext<GlobalStoreContextType>({
  state: initialState,
  fetchStats: () => { },
  loginUser: async () => ({ success: false }),
  logOutUser: async () => { },
});

export const Context = GlobalStoreContext; // Backward compatibility export
export default GlobalStore;